import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { dictionaries } from './dictionaries';
import { LOCALES, type Locale, type TranslationKey } from './types';

const STORAGE_KEY = 'nx.locale';

function detectLocale(initial?: Locale): Locale {
  if (initial && (LOCALES as readonly string[]).includes(initial)) return initial;
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && (LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
    const browser = window.navigator.language.toLowerCase();
    if (browser.startsWith('zh')) return 'zh';
    if (browser.startsWith('it')) return 'it';
  }
  return 'en';
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey) => string;
  format: {
    date: (value: Date) => string;
    number: (value: number) => string;
    currency: (value: number, currency?: string) => string;
  };
}

const I18nContext = createContext<I18nContextValue | null>(null);

export interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale(initialLocale));

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, locale);
      window.document.documentElement.setAttribute('lang', locale);
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const t = useCallback(
    (key: TranslationKey): string => {
      const dict = dictionaries[locale];
      return dict[key] ?? dictionaries.en[key] ?? String(key);
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      format: {
        date: (value: Date) =>
          new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(value),
        number: (value: number) => new Intl.NumberFormat(locale).format(value),
        currency: (value: number, currency = 'EUR') =>
          new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value),
      },
    }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an <I18nProvider>.');
  }
  return ctx;
}
